import { kpiChips, topIssues, noWebsiteInfo } from "./summaryModel";
import StrengthsStrip from "./StrengthsStrip";
import ReportCta from "./ReportCta";
import { S } from "./summaryCopy";
import { T } from "./i18n";

// "Das Wichtigste in 30 Sekunden": replaces the old "Ergebnis auf einen Blick"
// strip. Verdict and headline stay in the hero card above; this block adds the
// key numbers, the strengths, the three worst findings (title + why) and ONE
// primary button. Every value is free-payload data.
const CHIP_CLS = "rounded-full border px-3 py-1 text-sm font-medium";

// Plain color dots, not the bordered severity pill used by LockedFindings —
// this strip is a skim-in-30-seconds highlight, not the locked inventory
// further down the page, and using the same pill+card look for both made
// them read as the same block repeated (owner feedback, 2026-09-27).
const SEVERITY_DOT = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-500",
  low: "bg-slate-400",
};

function findingsTone([, critical, high]) {
  if (critical > 0) return "border-red-200 bg-red-50 text-red-800";
  if (high > 0) return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

export default function KeyTakeaways({ data, lang, token, headlineId, showCta }) {
  const C = S[lang] || S.de;
  const L = T[lang] || T.de;
  // The hero headline already states the Maps rank; the chip would repeat it.
  const chips = kpiChips(data).filter((c) => !(c.id === "maps" && String(headlineId).startsWith("maps")));
  const issues = topIssues(data, 3);
  const noSite = noWebsiteInfo(data);
  return (
    <section data-testid="key-takeaways" className="mt-6 rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
      <h2 className="mb-3 font-heading text-lg font-semibold sm:text-xl">{C.keyTakeaways.title}</h2>
      {noSite && noSite.lost > 0 && (
        <p data-testid="no-site-lost" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-lg font-semibold text-red-900">
          {C.noSite.lost(noSite.lost)}
        </p>
      )}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <span key={chip.id} className={`${CHIP_CLS} ${chip.id === "findings" ? findingsTone(chip.args) : "border-border bg-secondary text-secondary-foreground"}`}>
              {C.glance.chips[chip.id](...chip.args)}
            </span>
          ))}
        </div>
      )}
      <StrengthsStrip data={data} lang={lang} />
      {issues.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{C.keyTakeaways.issuesTitle}</h3>
          <ol className="mt-3 space-y-3">
            {issues.map((f) => (
              <li key={f.code || f.title} className="flex items-start gap-2.5">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[f.severity] || SEVERITY_DOT.low}`}
                  role="img"
                  aria-label={L.severity[f.severity] || L.severity.low}
                />
                <div className="min-w-0">
                  <span className="text-base font-medium">{f.title}</span>
                  {f.why && <p className="text-sm text-muted-foreground">{f.why}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {showCta && <ReportCta token={token} lang={lang} placement="top" className="mt-5" />}
    </section>
  );
}
